import { captureException } from '@sentry/react';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AIEvent,
  AIEventAgentFunctionCall,
  AIEventAgentFunctionResponse,
  AIEventAgentResponse,
  AIEventUserMessage,
  SessionResponse,
} from '@doger/core/shared/api';

import { AgentMessage } from './agent-message.component';
import { ThinkingIndicator } from './thinking-indicator.component';
import { UserMessage } from './user-message.component';
import styles from './chat-messages.module.scss';

type ChatMessagesProps = {
  className?: string;
  session: SessionResponse;
  isAgentResponding?: boolean;
};

export const ChatMessages = ({
  session,
  className,
  isAgentResponding,
}: ChatMessagesProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('core');

  const messages = React.useMemo(
    () =>
      session.events
        .flatMap((event) => {
          if (!event?.content?.parts?.length) {
            console.error('No parts found for event', event);
            captureException(
              new Error(
                '[ChatMessages] No parts found for event in session response'
              ),
              {
                extra: {
                  event,
                },
              }
            );

            return null;
          }

          return event.content.parts.map(
            (part, index): EventMessage => ({
              ...part,
              role: event.content.role,
              id: `${event.id}_${index}`,
            })
          );
        })
        .filter((event) => !!event),
    [session.events]
  );

  const thinkingIndicatorText = React.useMemo(() => {
    if (!isAgentResponding) return null;

    const lastMessage = messages[messages.length - 1];

    // If last message is a function call, show tool-specific text
    if (lastMessage && MessageDomain.isAgentFunctionCall(lastMessage)) {
      const toolName = lastMessage.functionCall?.name;

      return t(getToolDisplayText(toolName));
    }

    // If last message is a function response, show tool-specific analyzing text
    if (lastMessage && MessageDomain.isAgentFunctionResponse(lastMessage)) {
      const toolName = lastMessage.functionResponse?.name;

      return t(getToolAnalyzingText(toolName));
    }

    // Default thinking text
    return t('chat.thinking.default');
  }, [isAgentResponding, messages, t]);

  const lastAgentResponseIndex = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (MessageDomain.isAgentResponse(messages[i])) {
        return i;
      }
    }

    return -1;
  }, [messages]);

  return (
    <div className={clsx(styles.messagesArea, className)} ref={scrollAreaRef}>
      <div className={styles.messagesArea__container}>
        <div className={styles.messagesArea__content}>
          {messages.map((message, index) => {
            switch (true) {
              case MessageDomain.isUserMessage(message): {
                return <UserMessage key={message.id} message={message} />;
              }

              case MessageDomain.isAgentResponse(message): {
                return (
                  <AgentMessage
                    key={message.id}
                    message={message}
                    isLast={index === lastAgentResponseIndex}
                  />
                );
              }

              default: {
                return null;
              }
            }
          })}
          {thinkingIndicatorText && (
            <ThinkingIndicator text={thinkingIndicatorText} />
          )}
        </div>
      </div>
    </div>
  );
};

type EventMessage = AIEvent['content']['parts'][number] & {
  role: AIEvent['content']['role'];
  id: AIEvent['id'];
};

class MessageDomain {
  public static isUserMessage(
    message: EventMessage
  ): message is AIEventUserMessage {
    return message.role === 'user' && 'text' in message;
  }

  public static isAgentResponse(
    message: EventMessage
  ): message is AIEventAgentResponse {
    return message.role === 'model' && 'text' in message;
  }

  public static isAgentFunctionCall(
    message: EventMessage
  ): message is AIEventAgentFunctionCall {
    return message.role === 'model' && 'functionCall' in message;
  }

  public static isAgentFunctionResponse(
    message: EventMessage
  ): message is AIEventAgentFunctionResponse {
    return message.role === 'user' && 'functionResponse' in message;
  }
}

function getToolDisplayText(toolName: string): [string, string] {
  const toolDisplayMap: Record<string, string> = {
    dbsearch_tool: 'chat.thinking.tools.dbsearch_tool',
    company_check_tool: 'chat.thinking.tools.company_check_tool',
    websearch_tool: 'chat.thinking.tools.websearch_tool',
  };

  return [toolDisplayMap[toolName], 'chat.thinking.tools.unknown'];
}

function getToolAnalyzingText(toolName: string): [string, string] {
  const toolAnalyzingMap: Record<string, string> = {
    dbsearch_tool: 'chat.thinking.analyzing.dbsearch_tool',
    company_check_tool: 'chat.thinking.analyzing.company_check_tool',
    websearch_tool: 'chat.thinking.analyzing.websearch_tool',
  };

  return [toolAnalyzingMap[toolName], 'chat.thinking.analyzing.unknown'];
}
